if (!window.SmartTooltip) {
    class SmartTooltip extends HTMLElement {
        constructor() {
            super();
            this.handlePosition = this.handlePosition.bind(this);
            this.content = this.querySelector('.tooltip__content');
            this.defaultPosition = this.dataset.tooltipPosition || 'top';
            this.isMobile = window.innerWidth <= 768; // Add mobile detection
        }

        connectedCallback() {
            // Initial position check on mount
            requestAnimationFrame(() => {
                this.handlePosition();
            });

            // Recheck position on hover/focus
            this.addEventListener('mouseenter', this.handlePosition);
            this.addEventListener('focus', this.handlePosition);

            // Recheck on window resize
            window.addEventListener('resize', this.handlePosition);

            // Recheck on scroll of any parent that might affect positioning
            let parent = this.parentElement;
            while (parent) {
                if (getComputedStyle(parent).overflow !== 'visible') {
                    parent.addEventListener('scroll', this.handlePosition);
                }
                parent = parent.parentElement;
            }
        }

        disconnectedCallback() {
            this.removeEventListener('mouseenter', this.handlePosition);
            this.removeEventListener('focus', this.handlePosition);
            window.removeEventListener('resize', this.handlePosition);
        }

        handlePosition() {
            if (!this.content) return;

            // Reset to original position
            this.dataset.tooltipPosition = this.defaultPosition;
            
            // Get the tooltip and its dimensions
            const tooltip = this.content;
            const trigger = this.getBoundingClientRect();
            const tooltipRect = tooltip.getBoundingClientRect();
            
            // Get viewport dimensions and buffer
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const buffer = this.isMobile ? 15 : 10; // Increased buffer for mobile
            
            // Check for overflow and adjust position
            // Prioritize different positions for mobile vs desktop
            const positions = this.isMobile ? 
                ['left', 'right', 'bottom', 'top'] : // Mobile priorities
                ['top', 'right', 'bottom', 'left'];  // Desktop priorities
                
            let bestPosition = this.defaultPosition;
            let minOverflow = Infinity;
            let fallbackPosition = null;
            
            positions.forEach(position => {
                let overflow = 0;
                let rect = this.getPositionedRect(trigger, tooltipRect, position);
                
                // Calculate overflow for each edge, including buffer zone
                if (rect.left < buffer) overflow += Math.abs(rect.left) + buffer;
                if (rect.right > viewportWidth - buffer) overflow += rect.right - (viewportWidth - buffer);
                if (rect.top < buffer) overflow += Math.abs(rect.top) + buffer;
                if (rect.bottom > viewportHeight - buffer) overflow += rect.bottom - (viewportHeight - buffer);
                
                // For mobile, heavily penalize top position when near screen edges
                if (this.isMobile && position === 'top') {
                    const horizontalEdgeProximity = Math.min(
                        rect.left,
                        viewportWidth - rect.right
                    );
                    if (horizontalEdgeProximity < 50) { // If within 50px of screen edge
                        overflow += 1000; // Add significant penalty
                    }
                }
                
                // Update best position if this position has less overflow
                if (overflow < minOverflow) {
                    minOverflow = overflow;
                    bestPosition = position;
                }
                
                // Keep track of a fallback position (prefer left/right on mobile)
                if (this.isMobile && (position === 'left' || position === 'right') && overflow < Infinity) {
                    fallbackPosition = fallbackPosition || position;
                }
            });
            
            // If we're on mobile and the best position is causing significant overflow,
            // use the fallback position instead
            if (this.isMobile && minOverflow > 100 && fallbackPosition) {
                bestPosition = fallbackPosition;
            }
            
            // Apply the best position
            this.dataset.tooltipPosition = bestPosition;
        }
        
        getPositionedRect(trigger, tooltip, position) {
            let rect = {
                top: 0,
                right: 0,
                bottom: 0,
                left: 0
            };
            
            const spacing = this.isMobile ? 15 : 20; // Reduced spacing for mobile
            
            switch (position) {
                case 'top':
                    rect.top = trigger.top - tooltip.height - spacing;
                    rect.left = trigger.left + (trigger.width - tooltip.width) / 2;
                    break;
                case 'right':
                    rect.top = trigger.top + (trigger.height - tooltip.height) / 2;
                    rect.left = trigger.right + spacing;
                    break;
                case 'bottom':
                    rect.top = trigger.bottom + spacing;
                    rect.left = trigger.left + (trigger.width - tooltip.width) / 2;
                    break;
                case 'left':
                    rect.top = trigger.top + (trigger.height - tooltip.height) / 2;
                    rect.left = trigger.left - tooltip.width - spacing;
                    break;
            }
            
            rect.right = rect.left + tooltip.width;
            rect.bottom = rect.top + tooltip.height;
            
            return rect;
        }
    }
    window.SmartTooltip = SmartTooltip;
}

if (!customElements.get('smart-tooltip')) {
    customElements.define('smart-tooltip', SmartTooltip);
}